import { useEffect, useState } from "react"
import { BsArrowLeft, BsPlusCircleFill } from "react-icons/bs"
import { AiOutlineSearch, AiOutlineMail } from "react-icons/ai"
import { LiaExchangeAltSolid } from "react-icons/lia"
import { IoAddCircleSharp, IoChevronDownSharp } from "react-icons/io5"
import { IoMdCloseCircle, IoMdSettings } from "react-icons/io"
import dayjs from 'dayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import AddNewItem from "../components/AddNewItem"
import AddNewCustomer from "../components/AddNewCustomer"
import axios from "axios"
import api from "../config/backend"
import { useNavigate, useParams } from "react-router-dom"
import { SpinnerCircular } from "spinners-react"
import {
    Container,
    Grid,
    TextField,
} from '@mui/material'
import ManageTax from "./ManageTax"

const EditQuote = () => {
    const [customerInput, setCustomerInput] = useState("")
    const [customerInputOpen, setCustomerInputOpen] = useState(false)
    const [taxInputOpen, setTaxInputOpen] = useState(false)
    const [taxSearchInput, setTaxSearchInput] = useState("")
    const [referenceInput, setReferenceInput] = useState("")
    const [quoteDate, setQuoteDate] = useState(undefined)
    const [quoteExpiryDate, setQuoteExpiryDate] = useState(undefined)
    const [subjectInput, setSubjectInput] = useState("")
    const [quoteItems, setQuoteItems] = useState([
        {
            item: "",
            quantity: "1.00",
            rate: "0.00",
            data: undefined
        }
    ])
    const [discount, setDiscount] = useState("0")
    const [calculatedDiscount, setCalculatedDiscount] = useState("0")
    const [calculatedTax, setCalculatedTax] = useState(0)

    // Discount is percent or amount
    const [discountUnit, setDiscountUnit] = useState("percentage")
    const [adjustmentsText, setAdjustmentsText] = useState("Adjustments")
    const [adjustmentValue, setAdjustmentValue] = useState("0")
    const [subTotal, setSubTotal] = useState("0")
    const [totalAmount, setTotalAmount] = useState("0")
    const [customerNotes, setCustomerNotes] = useState("Looking forward for your business.")
    const [addItemsPopupIndex, setAddItemsPopupIndex] = useState(false)
    const [addNewItemPopupOpen, setAddNewItemPopupOpen] = useState(false)
    const [addNewCustomerPopup, setAddNewCustomerPopup] = useState(false)
    const [addManageTaxPopup, setAddManageTaxPopup] = useState(false)
    const [productList, setProductList] = useState([])
    const [filteredProductList, setFilteredProductList] = useState([])
    const [productUpdatedRefresh, setProductUpdatedRefresh] = useState(0)
    const [taxesUpdatedRefresh, setTaxesUpdatedRefresh] = useState(0)
    const [selectedTax, setSelectedTax] = useState(undefined)
    const [taxList, setTaxList] = useState([])
    const [filteredTaxList, setFilteredTaxList] = useState([])
    const [customersList, setCustomersList] = useState([])
    const [filteredCustomersList, setFilteredCustomersList] = useState([])
    const [selectedCustomer, setSelectedCustomer] = useState(undefined)
    const [customersUpdatedRefresh, setCustomersUpdatedRefresh] = useState(0)
    const [quoteCount, setQuoteCount] = useState(0)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(false)

    const token = localStorage.getItem("_crm__token")

    const { hash } = useParams()

    const navigate = useNavigate()

    const fetchProducts = async () => {
        const { data, status } = await axios.post(`${api}/Accounting/fetch-products`, { token })

        if (data.success) {
            setProductList(data.products)
        }
    }

    const fetchCustomers = async () => {
        const { data, status } = await axios.post(`${api}/Accounting/fetch-customers`, { token })

        if (data.success) {
            setCustomersList(data.customers)
        }
    }

    const fetchQuote = async () => {
        setLoading(true)
        const { data, status } = await axios.post(`${api}/Accounting/fetch-quote`, { token, hash })

        setLoading(false)

        if (data.success) {
            const quote = data.quote

            setSelectedCustomer(quote.customer)
            setQuoteCount(quote.quoteindex)
            setReferenceInput(quote.reference)
            setQuoteDate(dayjs(quote.quotedate))
            setQuoteExpiryDate(dayjs(quote.expirydate))
            setSubjectInput(quote.subject)
            setQuoteItems(quote.items)
            setDiscountUnit(quote.discount.ispercentage ? "percentage" : "amount")
            setDiscount(quote.discount.value)
            setAdjustmentsText(quote.adjustment.text)
            setAdjustmentValue(quote.adjustment.value)
            setCustomerNotes(quote.note)
            setSelectedTax(quote.taxdetails)
        }
    }

    const fetchTaxList = async () => {
        const { data, status } = await axios.post(`${api}/Accounting/fetch-tax`, {
            token,
            taxtype: "tcs"
        })

        if (data.success) {
            setTaxList(data.taxes)
        }
    }

    const handleEditQuote = async () => {
        const { data, status } = await axios.post(`${api}/Accounting/edit-quote`, {
            token,
            hash,
            customerhash: selectedCustomer && selectedCustomer.hash,
            reference: referenceInput,
            quotedate: quoteDate,
            expirydate: quoteExpiryDate,
            subject: subjectInput,
            items: quoteItems,
            discount: {
                ispercentage: discountUnit === "percentage" ? true : false,
                value: discount
            },
            adjustment: {
                text: adjustmentsText,
                value: adjustmentValue
            },
            taxhash: selectedTax ? selectedTax.hash : undefined,
            note: customerNotes
        })

        if (data.success) {
            const hash = data.quote.hash

            navigate("/accounting/quotes/create/" + hash)
        }
        else {
            setError(data.message)
        }
    }

    useEffect(() => {
        fetchQuote()
    }, [])

    useEffect(() => {
        fetchTaxList()
    }, [taxesUpdatedRefresh])

    useEffect(() => {
        fetchProducts()
    }, [productUpdatedRefresh])

    useEffect(() => {
        fetchCustomers()
    }, [customersUpdatedRefresh])

    // Calculate sub total amount
    useEffect(() => {
        let total = 0

        quoteItems.forEach(item => {
            total += item.quantity * item.rate
        })

        setSubTotal(total)
    }, [quoteItems])

    // Calculate discount
    useEffect(() => {
        if (discountUnit === "percentage") {
            setCalculatedDiscount((subTotal * discount) / 100)
        }
        else {
            setCalculatedDiscount(discount)
        }
    }, [subTotal, discount, discountUnit])

    // Calculate total amount
    useEffect(() => {
        const total = Number(subTotal) + Number(adjustmentValue) - Number(calculatedDiscount)

        let tax = 0

        if (selectedTax) {
            tax = total * selectedTax.rate / 100
        }

        setCalculatedTax(tax)
        setTotalAmount(total + tax)
    }, [subTotal, adjustmentValue, calculatedDiscount, selectedTax && selectedTax.hash])

    // Filter product list based on input
    useEffect(() => {
        if (addItemsPopupIndex === false) {
            return
        }

        setFilteredProductList(productList.filter(product => {
            const input = quoteItems[addItemsPopupIndex].item

            return product.productname.toLowerCase().includes(input.toLowerCase())
        }))
    }, [productList, quoteItems, addItemsPopupIndex])

    // Filter customers list based on search input
    useEffect(() => {
        if (customerInput) {
            setFilteredCustomersList(customersList.filter(customer => {
                const input = customerInput.toLowerCase()

                let fullname = customer.firstname.toLowerCase()

                if (customer.lastname) {
                    fullname = fullname + " " + customer.lastname.toLowerCase()
                }

                if (fullname.includes(input)) {
                    return true
                }

                if (customer.email.toLowerCase().includes(input)) {
                    return true
                }

                return false
            }))
        }
        else {
            setFilteredCustomersList(customersList)
        }
    }, [customerInput, customersList])

    // Filter tax list based on search input
    useEffect(() => {
        if (taxSearchInput) {
            setFilteredTaxList(taxList.filter(tax => {
                const input = taxSearchInput.toLowerCase()

                if (tax.name.toLowerCase().includes(input) || tax.tax.section.toLowerCase().includes(input)) {
                    return true
                }

                return false
            }))
        }
        else {
            setFilteredTaxList(taxList)
        }
    }, [taxSearchInput, taxList])

    useEffect(() => {
        let timeout

        if (error) {
            timeout = setTimeout(() => {
                setError(undefined)
            }, 4000)
        }

        return () => clearTimeout(timeout)
    }, [error])

    return (
        (
            <div className="_7zhg">
                {addNewCustomerPopup ? <AddNewCustomer setOpen={setAddNewCustomerPopup} setCustomersUpdatedRefresh={setCustomersUpdatedRefresh} /> : undefined}
                {addNewItemPopupOpen ? <AddNewItem open={addNewItemPopupOpen} setOpen={setAddNewItemPopupOpen} setProductUpdatedRefresh={setProductUpdatedRefresh} /> : undefined}
                <ManageTax popup={addManageTaxPopup} setPopup={setAddManageTaxPopup} setTriggerRefresh={setTaxesUpdatedRefresh} taxtype="tcs" />
               <Container>
               <div className="header">
                    <BsArrowLeft onClick={() => navigate("/accounting/quotes")} />
                    <span>Edit Quote</span>
                    {error ? (
                        <div className="dangle-fog">
                            {error}
                        </div>
                    ) : undefined}
                </div>
                {loading ? (
                    <div className="alcopop-ash">
                        <SpinnerCircular size="70" color="#3b70ad" />
                    </div>
                ) : (
                    <div className="soothe-far" onClick={() => {
                        customerInputOpen && setCustomerInputOpen(false)
                        addItemsPopupIndex !== false && setAddItemsPopupIndex(false)
                    }}>
                        <div className="row">
                            <div className="whingy-peep">
                                <div className="poortith-loch">
                                    <div onClick={() => setCustomerInputOpen(true)} className="salients-rap">
                                        {selectedCustomer ? (
                                            selectedCustomer.firstname + " " + selectedCustomer.lastname
                                        ) : "Select or add a customer"}
                                    </div>
                                    <button onClick={() => setCustomerInputOpen(true)}>
                                        <AiOutlineSearch />
                                    </button>
                                    {customerInputOpen ? <div onClick={e => e.stopPropagation()} className="cirrus-tee">
                                        <input
                                            value={customerInput}
                                            onChange={e => setCustomerInput(e.target.value)}
                                            placeholder="Search"
                                        />
                                        <div className="pullmans-tom">
                                            {filteredCustomersList.length ? (
                                                filteredCustomersList.map((customer, customerIndex) => {
                                                    return (
                                                        <div key={customerIndex} className="poetise-row" onClick={() => {
                                                            setSelectedCustomer(customer)
                                                            setCustomerInputOpen(false)
                                                        }}>
                                                            <div className="winked-flit">
                                                                {customer.firstname} {customer.lastname}
                                                            </div>
                                                            <div className="wights-spar">
                                                                <AiOutlineMail /> {customer.email}
                                                            </div>
                                                            <div className="grifting-dew">
                                                                {customer.currency.code}
                                                            </div>
                                                        </div>
                                                    )
                                                })
                                            ) : (
                                                <div className="toffies-kook">
                                                    No customers
                                                </div>
                                            )}
                                        </div>
                                        <div className="knife-labs" onClick={() => setAddNewCustomerPopup(true)}>
                                            <IoAddCircleSharp />
                                            <span>New Customer</span>
                                        </div>
                                    </div> : undefined}
                                </div>
                                {selectedCustomer ? (
                                    <div className="bedirty-rub">
                                        {selectedCustomer.currency.icon} {selectedCustomer.currency.code}
                                    </div>
                                ) : undefined}
                            </div>
                        </div>
                        <Grid container spacing={2}>
                            <Grid item xs={4}>
                                <div className="row">
                                    <div className="whingy-peep">
                                        <span>Quote</span>
                                        <div className="woolsack-cue">QT-{String(quoteCount).padStart(6, "0")}</div>
                                    </div>
                                </div>
                            </Grid>
                            <Grid item xs={4}>
                                <div className='primary-first-name'>
                                    <TextField value={referenceInput} onChange={e => setReferenceInput(e.target.value)} id="outlined-basic" label="Reference" variant="outlined" />
                                </div>
                            </Grid>
                        </Grid>
                        {/* 
                        <div className="row">
                            <div className="whingy-peep">
                                <span>Reference</span>
                                <input
                                    className="exabyte-hop woolsack-cue"
                                    value={referenceInput}
                                    onChange={e => setReferenceInput(e.target.value)}
                                />
                            </div>
                        </div> */}
                        {/* <div className="row">
                            <div className="whingy-peep">
                                <span>Quote Date</span>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="Quote Date"
                                        value={quoteDate}
                                        onChange={(newValue) => setQuoteDate(newValue)}
                                    />
                                </LocalizationProvider>
                            </div>
                            <div className="whingy-peep">
                                <span>Expiry Date</span>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="Expiry Date"
                                        value={quoteExpiryDate}
                                        onChange={(newValue) => setQuoteExpiryDate(newValue)}
                                    />
                                </LocalizationProvider>
                            </div>
                        </div> */}
                        <Grid container spacing={2}>
                            <Grid item xs={4}>
                                <div className='primary-first-name'>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DatePicker
                                            label="Quote Date"
                                            value={quoteDate}
                                            onChange={(newValue) => setQuoteDate(newValue)}
                                        />
                                    </LocalizationProvider>
                                </div>
                            </Grid>
                            <Grid item xs={4}>
                                <div className='primary-first-name'>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DatePicker
                                            label="Expiry Date"
                                            value={quoteExpiryDate}
                                            onChange={(newValue) => setQuoteExpiryDate(newValue)}
                                        />
                                    </LocalizationProvider>
                                </div>
                            </Grid>
                        </Grid>
                        <Grid container spacing={2}>
                            <Grid item xs={4}>
                                <div className='primary-first-name'>
                                    <TextField value={subjectInput}
                                        onChange={e => setSubjectInput(e.target.value)} placeholder="Let the customer know what the quote is for" id="outlined-basic" label="Subject" variant="outlined" />
                                </div>
                            </Grid>
                        </Grid>
                        {/* <div className="row">
                            <div className="whingy-peep">
                                <span>Subject</span>
                                <input
                                    style={{ width: "400px" }}
                                    className="exabyte-hop woolsack-cue"
                                    value={subjectInput}
                                    onChange={e => setSubjectInput(e.target.value)}
                                    placeholder="Let the customer know what the quote is for"
                                />
                            </div>
                        </div> */}
                        <div className="row">
                            <div className="murphies-nut">
                                <div className="table-header">
                                    <div className="item-details">
                                        ITEM DETAILS
                                    </div>
                                    <div>
                                        QUANTITY
                                    </div>
                                    <div>
                                        RATE
                                    </div>
                                    <div>
                                        AMOUNT
                                    </div>
                                </div>
                                <div className="table-content">
                                    {quoteItems.map((item, index) => {
                                        return (
                                            <div key={index} className="table-row">
                                                <div className="item-details" onClick={e => {
                                                    e.stopPropagation()
                                                    setAddItemsPopupIndex(index)
                                                }}>
                                                    <textarea
                                                        className="serif-rags"
                                                        value={item.item}
                                                        onChange={e => setQuoteItems(prev => {
                                                            const copy = JSON.parse(JSON.stringify(prev))
                                                            copy[index].item = e.target.value

                                                            // Remove the prev data - adds up because of selecting a product
                                                            copy[index].data = undefined
                                                            return copy
                                                        })}
                                                        placeholder="Type or click to select an item."
                                                    />
                                                    {addItemsPopupIndex === index ? (
                                                        <div className="foreguts-pen" onClick={e => e.stopPropagation()}>
                                                            {filteredProductList.length ? filteredProductList.map((product, productIndex) => {
                                                                return (
                                                                    <div key={productIndex} className="anarch-oat" onClick={() => {
                                                                        setQuoteItems(prev => {
                                                                            const copy = JSON.parse(JSON.stringify(prev))
                                                                            copy[index].data = product
                                                                            copy[index].item = product.productname
                                                                            copy[index].rate = product.sellingprice
                                                                            return copy
                                                                        })
                                                                        setAddItemsPopupIndex(false)
                                                                    }}>
                                                                        <span>{product.productname}</span>
                                                                        <span>Rate: {product.sellingprice}</span>
                                                                    </div>
                                                                )
                                                            }) : (
                                                                <div>
                                                                    No products
                                                                </div>
                                                            )}
                                                            <div className="freeway-goal" onClick={() => setAddNewItemPopupOpen(true)}>
                                                                <IoAddCircleSharp />
                                                                <span>Add new item</span>
                                                            </div>
                                                        </div>
                                                    ) : undefined}
                                                </div>
                                                <div>
                                                    <input
                                                        className="liftoff-just"
                                                        value={item.quantity}
                                                        onChange={e => setQuoteItems(prev => {
                                                            const copy = JSON.parse(JSON.stringify(prev))
                                                            copy[index].quantity = e.target.value

                                                            return copy
                                                        })}
                                                    />
                                                </div>
                                                <div>
                                                    <input
                                                        className="liftoff-just"
                                                        value={item.rate}
                                                        onChange={e => setQuoteItems(prev => {
                                                            const copy = JSON.parse(JSON.stringify(prev))
                                                            copy[index].rate = e.target.value

                                                            return copy
                                                        })}
                                                    />
                                                </div>
                                                <div className="upholder-eyed">
                                                    {(Math.round((item.rate * item.quantity) * 100) / 100).toFixed(2)}
                                                </div>
                                                <div className="hominids-cads" onClick={() => setQuoteItems(prev => {
                                                    const copy = JSON.parse(JSON.stringify(prev))
                                                    copy.splice(index, 1)

                                                    return copy
                                                })}>
                                                    <IoMdCloseCircle />
                                                </div>
                                            </div>
                                        )
                                    })
                                    }
                                </div>
                            </div>
                        </div>
                        <div className="files-skis row">
                            <div className="agitator-ask">
                                <div>
                                    <button className="welsh-cubs" onClick={() => setQuoteItems(prev => {
                                        const copy = JSON.parse(JSON.stringify(prev))
                                        copy.push({
                                            item: "",
                                            quantity: "1.00",
                                            rate: "0.00",
                                            data: undefined
                                        })
                                        return copy
                                    })}>
                                        <BsPlusCircleFill />
                                        <span>Add another line</span>
                                    </button>
                                </div>
                                <div>
                                    <div>Customer Notes</div>
                                    <textarea
                                        value={customerNotes}
                                        onChange={e => setCustomerNotes(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="adepts-hope">
                                <div className="imitator-doty">
                                    <div className="atheise-heir networks-oak">
                                        <div>
                                            Sub Total
                                        </div>
                                        <div className="icing-bit">

                                        </div>
                                        <div>
                                            {(Math.round(subTotal * 100) / 100).toFixed(2)}
                                        </div>
                                    </div>
                                    <div className="networks-oak">
                                        <div>
                                            Discount
                                        </div>
                                        <div className="jibes-het icing-bit">
                                            <input
                                                value={discount}
                                                onChange={e => setDiscount(e.target.value)}
                                            />
                                            <div className="shallops-onus">
                                                <span>{discountUnit === "percentage" ? "%" : "₹"}</span>
                                                <div className="godetias-doom" onClick={() => setDiscountUnit(prev => {
                                                    return prev === "percentage" ? "amount" : "percentage"
                                                })}>
                                                    <LiaExchangeAltSolid />
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            {(Math.round(calculatedDiscount * 100) / 100) > 0 ? "-" : undefined}{(Math.round(calculatedDiscount * 100) / 100).toFixed(2)}
                                        </div>
                                    </div>
                                    <div className="networks-oak">
                                        <div>
                                            <input
                                                className="coxalgic-say"
                                                value={adjustmentsText}
                                                onChange={e => setAdjustmentsText(e.target.value)}
                                            />
                                        </div>
                                        <div className="icing-bit">
                                            <input
                                                value={adjustmentValue}
                                                onChange={e => setAdjustmentValue(e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            {(Math.round(adjustmentValue * 100) / 100).toFixed(2)}
                                        </div>
                                    </div>
                                    <div className="networks-oak">
                                        <div>
                                            TCS
                                        </div>
                                        <div className="sooty-few icing-bit">
                                            <div className="stopt-gnu" onClick={() => setTaxInputOpen(prev => !prev)}>
                                                <span>{selectedTax ? selectedTax.name : "Select a Tax"}</span>
                                                <IoChevronDownSharp />

                                                {taxInputOpen ? <div onClick={e => e.stopPropagation()} className="chimeras-time cirrus-tee">
                                                    <input
                                                        value={taxSearchInput}
                                                        onChange={e => setTaxSearchInput(e.target.value)}
                                                        placeholder="Search"
                                                    />
                                                    <div className="pullmans-tom">
                                                        {filteredTaxList.length ? (
                                                            filteredTaxList.map((tax, taxIndex) => {
                                                                return (
                                                                    <div key={taxIndex} className="poetise-row" onClick={() => {
                                                                        setSelectedTax(tax)
                                                                        setTaxInputOpen(false)
                                                                    }}>
                                                                        <div className="winked-flit">
                                                                            {tax.name}
                                                                        </div>
                                                                        <div className="wights-spar">
                                                                            <AiOutlineMail /> {tax.tax.section}
                                                                        </div>
                                                                        <div className="grifting-dew">
                                                                            {tax.rate}%
                                                                        </div>
                                                                    </div>
                                                                )
                                                            })
                                                        ) : (
                                                            <div className="toffies-kook">
                                                                No results found
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="knife-labs" onClick={() => setAddManageTaxPopup(true)}>
                                                        <IoMdSettings />
                                                        <span>Manage TCS</span>
                                                    </div>
                                                </div> : undefined}
                                            </div>
                                            {selectedTax ? <div onClick={() => setSelectedTax(undefined)} className="fecal-aha">Remove</div> : undefined}
                                        </div>
                                        <div>
                                            {(Math.round(calculatedTax * 100) / 100).toFixed(2)}
                                        </div>
                                    </div>
                                    <div className="mulling-guy networks-oak">
                                        <div>
                                            Total
                                        </div>
                                        <div className="icing-bit">

                                        </div>
                                        <div>
                                            ₹ {(Math.round(totalAmount * 100) / 100).toFixed(2)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="footrot-pip">
                            <button className="ballroom-tab" onClick={() => handleEditQuote()}>
                                Save and Send
                            </button>
                            <button className="satin-net" onClick={() => navigate("/accounting/quotes")}>
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
               </Container>
            </div >
        )
    )
}

export default EditQuote
